<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div class="trezo-card-content">
      <div v-if="loading" class="text-center py-5">
        <div class="inline-block animate-spin rounded-full border-4 border-primary-500 border-t-transparent h-12 w-12 mb-4"></div>
        <p class="text-gray-500 dark:text-gray-400">Loading notifications...</p>
      </div>
      <div v-else-if="errorMessage" class="text-center py-5 text-danger-500">
        {{ errorMessage }}
      </div>
      <div v-else-if="notifications.length === 0" class="text-center py-5">
        <p class="text-gray-500 dark:text-gray-400">No notifications found</p>
      </div>
      <div v-else class="table-responsive overflow-x-auto">
        <table class="w-full">
          <thead class="text-black dark:text-white">
            <tr>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap ltr:first:rounded-tl-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md"
              >
                ID
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap ltr:first:rounded-tl-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md"
              >
                Timestamp
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap ltr:first:rounded-tl-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md"
              >
                Type
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap ltr:first:rounded-tl-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md"
              >
                Content
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap ltr:first:rounded-tl-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md"
              >
                Status
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap ltr:first:rounded-tl-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md"
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody class="text-black dark:text-white">
            <tr v-for="notification in notifications" :key="notification.id">
              <td
                class="text-gray-500 dark:text-gray-400 ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                {{ notification.id.slice(0, 8) }}
              </td>
              <td
                class="text-gray-500 dark:text-gray-400 ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                {{ formatDate(notification.timestamp) }}
              </td>
              <td
                class="font-medium ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                {{ capitalizeFirstLetter(notification.type) }}
              </td>
              <td
                class="font-medium ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                {{ notification.content }}
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                <span
                  :class="{
                    'px-[8px] py-[3px] inline-block rounded-sm font-medium text-xs': true,
                    'bg-danger-50 dark:bg-[#15203c] text-danger-500': notification.status === 'unread',
                    'bg-primary-50 dark:bg-[#15203c] text-primary-500': notification.status === 'read'
                  }"
                >
                  {{ capitalizeFirstLetter(notification.status) }}
                </span>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                <button
                  type="button"
                  class="text-primary-500 leading-none custom-tooltip"
                  id="customTooltip"
                  data-text="View"
                  @click="markAsRead(notification)"
                >
                  <i class="material-symbols-outlined !text-md"> visibility </i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted } from "vue";
import { supabase } from "@/utils/supabase";

interface Notification {
  id: string;
  userid: string;
  timestamp: string;
  type: string;
  content: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export default defineComponent({
  // eslint-disable-next-line vue/multi-word-component-names
  name: "Notifications",
  setup() {
    const notifications = ref<Notification[]>([]);
    const loading = ref(true);
    const errorMessage = ref("");
    let notificationsSubscription: any = null;
    let refreshInterval: number | null = null;

    const fetchNotifications = async () => {
      try {
        loading.value = true;
        errorMessage.value = "";
        
        const { data: userData, error: userError } = await supabase.auth.getUser();
        
        if (userError) {
          throw new Error(userError.message);
        }
        
        if (!userData || !userData.user) {
          throw new Error("No authenticated user found");
        }
        
        const { data, error } = await supabase
          .from('notifications')
          .select('*')
          .eq('userid', userData.user.id)
          .order('timestamp', { ascending: false });
          
        if (error) {
          throw new Error(error.message);
        }
        
        notifications.value = (data as any[]) || [];
      } catch (error) {
        console.error("Error fetching notifications:", error);
        errorMessage.value = error instanceof Error ? error.message : "Failed to load notifications";
      } finally {
        loading.value = false;
      }
    };

    const markAsRead = async (notification: Notification) => {
      if (notification.status === 'read') return;
      
      try {
        console.log("Marking notification as read:", notification.id);
        
        const { error } = await supabase
          .from('notifications')
          .update({ status: 'read' })
          .eq('id', notification.id);
          
        if (error) {
          console.error("Failed to update notification:", error);
          throw new Error(error.message);
        }
        
        console.log("Notification marked as read successfully");
        
        // רענן את רשימת ההתראות כדי לוודא שיש לנו את המידע העדכני ביותר
        await fetchNotifications();
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    };

    // הוספת Supabase Realtime הקשבה לשינויים
    const setupRealtimeListener = async () => {
      try {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData || !userData.user) return;
        
        // הקשבה לטבלת notifications
        notificationsSubscription = supabase
          .channel('notifications-page-changes')
          .on('postgres_changes', 
            { 
              event: '*', 
              schema: 'public', 
              table: 'notifications',
              filter: `userid=eq.${userData.user.id}`
            }, 
            (payload) => {
              console.log('Realtime notification change (full page):', payload);
              
              // רענון ההתראות כשיש שינוי
              fetchNotifications();
            }
          )
          .subscribe();
          
        console.log('Realtime subscription setup for notifications page');
      } catch (err) {
        console.error('Error setting up realtime listener:', err);
      }
    };
    
    // הוספת רענון עיתי
    const setupRefreshInterval = () => {
      refreshInterval = window.setInterval(() => {
        fetchNotifications();
      }, 30000); // רענון כל 30 שניות
    };

    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const capitalizeFirstLetter = (string: string) => {
      return string.charAt(0).toUpperCase() + string.slice(1);
    };

    onMounted(() => {
      fetchNotifications();
      setupRealtimeListener();
      setupRefreshInterval();
    });
    
    onUnmounted(() => {
      // ניקוי הקשבה בזמן אמת ורענון עיתי
      if (notificationsSubscription) {
        notificationsSubscription.unsubscribe();
      }
      
      if (refreshInterval !== null) {
        clearInterval(refreshInterval);
      }
    });

    return {
      notifications,
      loading,
      errorMessage,
      markAsRead,
      formatDate,
      capitalizeFirstLetter
    };
  }
});
</script>
